import React from "react";
import Downshift, {
  DownshiftInterface,
  GetInputPropsOptions,
  ControllerStateAndHelpers,
} from "downshift";
import { GeoSearchRequester, GeoSearchResults } from "@justfixnyc/geosearch-requester";

import "../styles/AddressSearch.css";
import { Borough } from "./APIDataTypes";

const GeoDownshift = Downshift as DownshiftInterface<SearchAddress>;

const KEY_ENTER = 13;

const KEY_TAB = 9;

const KEY_ESC = 27;

const SEARCH_RESULTS_LIMIT = 5;

export interface SearchAddress {
  /**
   * The house number, e.g. '654'. It can be undefined,
   * e.g. for NYCHA properties.
   */
  housenumber?: string;

  /** The street name, e.g. 'PARK PLACE'. */
  streetname: string;

  /** The all-uppercase borough name, e.g. 'BROOKLYN'.
   * We allow for an empty string case in this type as our GeoSearch form submission includes the possibility
   * of an 'empty' address search. See the AddressSearch class below for more details.
   */
  boro: Borough | "";

  /** The padded BBL, e.g. '1234567890'. */
  bbl: string;
}

export interface AddressSearchProps {
  onFormSubmit: (searchAddress: SearchAddress, error: any) => void;
  labelText: string | JSX.Element;
  labelClass: string;
  placeholder?: string;
  onInputChange?: (value: string) => void;
  /** Called when the GeoSearch autocomplete API fails (e.g. 503). Not a form submission. */
  onSearchError?: (error: any) => void;
}

type State = {
  isLoading: boolean;
  results: SearchAddress[];
  /** After a dropdown selection, the next focus/click clears the input. */
  clearOnNextFocus: boolean;
};

/**
 * Return an empty search address.
 *
 * This could just be a constant but I'm not confident that the
 * code which calls it won't mutate it, so we'll just create a
 * new one every time. -AV
 */
export function makeEmptySearchAddress(): SearchAddress {
  return {
    housenumber: "",
    streetname: "",
    boro: "",
    bbl: "",
  };
}

function toSearchAddresses(results: GeoSearchResults): SearchAddress[] {
  return results.features.map((feature) => {
    let formattedBoroName = feature.properties.borough.toUpperCase() as Borough;
    const sa: SearchAddress = {
      housenumber: feature.properties.housenumber,
      streetname: feature.properties.street,
      boro: formattedBoroName,
      bbl: feature.properties.addendum.pad.bbl,
    };
    return sa;
  });
}

export function searchAddressToString(sa: SearchAddress): string {
  const prefix = sa.housenumber ? `${sa.housenumber} ` : "";
  return `${prefix}${sa.streetname}, ${sa.boro}`;
}

export default class AddressSearch extends React.Component<AddressSearchProps, State> {
  requester: GeoSearchRequester;

  constructor(props: AddressSearchProps) {
    super(props);
    this.state = {
      isLoading: false,
      results: [],
      clearOnNextFocus: false,
    };
    this.requester = new GeoSearchRequester({
      onError: (e) => {
        this.setState({ isLoading: false, results: [] });
        this.props.onSearchError?.(e);
      },
      onResults: (results) => {
        this.setState({
          isLoading: false,
          results: toSearchAddresses(results).slice(0, SEARCH_RESULTS_LIMIT),
        });
      },
    });
  }

  componentWillUnmount() {
    this.requester.shutdown();
  }

  handleInputValueChange(value: string) {
    if (this.state.clearOnNextFocus) {
      this.setState({ clearOnNextFocus: false });
    }
    this.props.onInputChange?.(value);
    if (this.requester.changeSearchRequest(value)) {
      this.setState({ isLoading: true });
    } else {
      this.setState({ isLoading: false, results: [] });
    }
  }

  /**
   * If the result list is non-empty and visible, and the user hasn't selected
   * anything, select the first item in the list and return true.
   *
   * Otherwise, return false.
   */
  selectFirstResult(ds: ControllerStateAndHelpers<SearchAddress>): boolean {
    const { results } = this.state;
    if (ds.highlightedIndex === null && ds.isOpen && results.length > 0) {
      ds.selectItem(results[0]);
      return true;
    }
    return false;
  }

  handleAutocompleteKeyDown(
    ds: ControllerStateAndHelpers<SearchAddress>,
    event: React.KeyboardEvent
  ) {
    const { results } = this.state;
    if (event.keyCode === KEY_ENTER) {
      if (this.selectFirstResult(ds)) {
        event.preventDefault();
      }
    }
    if (results.length > 0) {
      // Allow tab key to navigate to next item in autocomplete list
      if (event.keyCode === KEY_TAB && !event.shiftKey) {
        ds.setHighlightedIndex(
          ds.highlightedIndex === results.length - 1 || ds.highlightedIndex === null
            ? 0
            : ds.highlightedIndex + 1
        );
        event.preventDefault();
      }
      // Allow tab key + shift key to navigate to previous item in autocomplete list
      if (event.keyCode === KEY_TAB && event.shiftKey) {
        ds.setHighlightedIndex(
          ds.highlightedIndex === 0 || ds.highlightedIndex === null
            ? results.length - 1
            : ds.highlightedIndex - 1
        );
        event.preventDefault();
      }
    }
    // Allow esc key to clear the results
    // Note: we do not call `event.preventDefault()` here as we also want to allow the event of
    // pressing the esc key to trigger its default responses as well (like clearing the search form)
    if (event.keyCode === KEY_ESC) {
      this.setState({
        results: [],
      });
    }
  }

  render() {
    return (
      <GeoDownshift
        stateReducer={(state, changes) => {
          switch (changes.type) {
            case Downshift.stateChangeTypes.mouseUp:
            case Downshift.stateChangeTypes.touchEnd:
            case Downshift.stateChangeTypes.blurInput:
              // By default, Downshift clears the input value,
              // but we don't want to lose user data, so we'll
              // override that behavior here.
              return {
                ...changes,
                inputValue: state.inputValue,
              };

            default:
              return changes;
          }
        }}
        onChange={(sa) => {
          if (sa) {
            // Clear the suggestion list so the dropdown hides after a
            // selection (visibility also depends on results.length > 0).
            this.setState({ results: [] });
            this.props.onFormSubmit(sa, null);
            // Defer so Downshift's post-select focus doesn't clear the
            // selected address immediately; the next user focus will.
            setTimeout(() => {
              this.setState({ clearOnNextFocus: true });
            }, 0);
          }
          // TODO: I am very unclear on what it means for `sa` to be null,
          // and the docs don't seem to provide any guidance on the matter.
          // There's no meaningful value for us to pass to `onFormSubmit` in
          // this case, so we will just do nothing.
        }}
        itemToString={(sa) => {
          return sa ? searchAddressToString(sa) : "";
        }}
      >
        {(downshift) => {
          const inputOptions: GetInputPropsOptions = {
            onKeyDown: (e) => this.handleAutocompleteKeyDown(downshift, e),
            onChange: (e) => this.handleInputValueChange(e.currentTarget.value),
            onFocus: () => {
              if (this.state.clearOnNextFocus) {
                downshift.clearSelection();
                this.setState({ clearOnNextFocus: false, results: [] });
                this.handleInputValueChange("");
              }
            },
          };
          const suggestsClasses = ["geosuggest__suggests"];
          if (!(downshift.isOpen && this.state.results.length > 0)) {
            suggestsClasses.push("geosuggest__suggests--hidden");
          }

          return (
            <div className="AddressSearch">
              <div className="form-group col-xs-12">
                <div className="geosuggest">
                  <div className="geosuggest__input-wrapper">
                    <label className={this.props.labelClass} {...downshift.getLabelProps()}>
                      {this.props.labelText}
                    </label>
                    <input
                      autoFocus
                      placeholder={this.props.placeholder ?? "Search places"}
                      className="geosuggest__input form-input"
                      {...downshift.getInputProps(inputOptions)}
                    />
                  </div>
                  <div className="geosuggest__suggests-wrapper">
                    <ul className={suggestsClasses.join(" ")} {...downshift.getMenuProps()}>
                      {this.state.results.map((item, index) => {
                        const classes = ["geosuggest__item"];
                        if (downshift.highlightedIndex === index) {
                          classes.push("geosuggest__item--active");
                        }
                        const label = searchAddressToString(item);
                        const props = downshift.getItemProps({
                          key: label,
                          index,
                          item,
                        });
                        return (
                          <li className={classes.join(" ")} {...props}>
                            <span>{label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </GeoDownshift>
    );
  }
}
